/**
 * 맞은 자리에서 떠오르는 데미지 숫자.
 *
 * 시뮬레이션에서 체력이 줄어드는 곳은 피해 판정 하나뿐이다(`World.applyDamage`). 그래서 렌더러는 프레임마다
 * 체력이 줄어든 만큼을 읽기만 하면 되고, 시뮬레이션에 사건 기록을 따로 둘 필요가 없다 — 리플레이 형식도 그대로다.
 * 연출 전용이라 흔들림에 `Math.random`을 쓴다.
 */
import { Container, Rectangle, Sprite, Texture } from 'pixi.js';

/** 떠 있는 숫자의 최대 수. 넘치면 가장 오래된 것을 다시 쓴다. */
export const MAX_FLOATS = 72;
/** 한 프레임에 새로 띄울 수 있는 수. 휩쓸기 한 번에 여럿이 쓰러지는 난전에서 비용을 묶어둔다. */
export const FLOAT_BUDGET = 14;
const LIFE_MS = 720;
/** 이 시간 동안 조금 크게 튀어나왔다가 제 크기로 돌아온다. */
const POP_MS = 90;
const RISE_PX = 13;
/** 머리 위(월드 픽셀). 발 기준 위치에서 이만큼 올라간 곳에서 뜬다. */
const HEAD_PX = 20;
const MAX_DIGITS = 4;
/** 같은 프레임에 뜬 장수 숫자를 비켜 쌓는 폭(월드 픽셀). 세로는 두 배 크기 글자 한 줄(7×2)에서 테두리 한 칸을 겹친다. */
const BIG_STAGGER_X = 5;
const BIG_STAGGER_Y = 12;

/** 적이 입은 피해 — 내가 준 피해다. */
export const DAMAGE_DEALT_COLOR = 0xfff1c4;
/** 내 편이 입은 피해. */
export const DAMAGE_TAKEN_COLOR = 0xff6b5a;
/** 장수가 친 피해. 크게(정수배 2) 띄운다 — 일당백의 손맛은 장수의 칼에서 나온다. */
export const DAMAGE_LEADER_COLOR = 0xffa531;
/** 장수가 한 방에 쓰러뜨렸으면 숫자가 처음 이만큼 흰빛과 제 빛을 번갈아 번쩍인다. */
const FLASH_MS = 200;
const FLASH_BLINK_MS = 50;

/** 쓰러뜨린 숫자가 떠오른 지 `t`ms에 흰빛인가. 흰빛으로 시작해 50ms마다 바뀌고 200ms 뒤로는 제 빛이다. */
export function flashLit(t: number): boolean {
  return t < FLASH_MS && Math.floor(t / FLASH_BLINK_MS) % 2 === 0;
}

/**
 * 3×5 도트 숫자. 한 줄이 가로 세 칸이고 1이 칠한 칸이다. 테두리를 한 칸씩 둘러 5×7로 굽는다 — 흙빛 땅과
 * 피 얼룩 위에서도 읽히게.
 */
export const GLYPHS: readonly string[] = [
  '111101101101111', // 0
  '010110010010111', // 1
  '111001111100111', // 2
  '111001111001111', // 3
  '101101111001001', // 4
  '111100111001111', // 5
  '111100111101111', // 6
  '111001001010010', // 7
  '111101111101111', // 8
  '111101111001111', // 9
];
const GW = 3;
const GH = 5;
/** 테두리 포함 한 글자 칸. */
const CELL_W = GW + 2;
const CELL_H = GH + 2;
/** 글자 사이 간격. 테두리끼리 겹치게 붙여 숫자가 한 덩어리로 읽힌다. */
const ADVANCE = GW + 1;

/** `scanDamage`가 읽는 병사 상태. `World`가 그대로 맞는다. */
export interface HpView {
  readonly count: number;
  readonly hp: Int32Array;
  readonly alive: Uint8Array;
  /** 장수에게서 받은 피해의 누계(`World.leaderDealt`). */
  readonly leaderDealt: Int32Array;
}

/** `scanDamage`가 병사마다 기억하는 기준값. */
export interface HpSeen {
  readonly hp: Int32Array;
  readonly leader: Int32Array;
  readonly live: Uint8Array;
}

/**
 * 직전 프레임보다 체력이 줄어든 병사마다 `emit(i, 다른 이의 피해, 장수의 피해)`를 부르고, 다음 프레임에 어디서부터
 * 볼지를 돌려준다. 장수 몫은 `leaderDealt`가 늘어난 만큼이고 나머지가 다른 이의 몫이다. 장수 몫은 한도 없이
 * 늘 띄운다고 보고, `emit`의 false는 **다른 이의 몫을** 띄우지 못했다는 뜻이다.
 *
 * 체력은 피해 판정에서만 줄어들므로 줄어든 만큼이 곧 입은 피해다. 쓰러뜨린 한 방은 남은 체력보다 클 수 있어
 * 음수까지 그대로 뺀다. 살아 있지 않던 자리에 새로 나온 병사는 체력이 채워진 것이지 맞은 것이 아니다(`live`).
 *
 * `emit`이 false(이번 프레임 한도가 찼다)를 돌려주면 다른 이의 몫만큼 기준 체력을 남겨 **다음 프레임으로 미룬다** —
 * 피해가 쌓여 한 숫자로 뜬다. 장수 몫은 이미 떴으니 기준에서 뺀다. 처음에는 장수 몫이 섞이면 무조건 다 띄운 것으로
 * 쳐서, 한도가 찬 프레임에 다른 이의 몫이 조용히 사라졌다(리뷰가 짚었다). 다음 프레임은 미룬 첫 병사부터 본다. 처음에는 늘 색인 0부터 보고 못 띄운 것은
 * 버렸는데, 색인이 아군 → 적 순이라 아군이 여럿 맞는 프레임에는 적이 입은 숫자가 통째로 빠졌다(리뷰가 짚었다).
 */
export function scanDamage(
  w: HpView,
  seen: HpSeen,
  cursor: number,
  emit: (i: number, other: number, leader: number) => boolean,
): number {
  const n = w.count;
  const start = cursor < n ? cursor : 0;
  let next = -1;
  for (let k = 0; k < n; k++) {
    const i = start + k < n ? start + k : start + k - n;
    const hp = w.hp[i];
    const lead = w.leaderDealt[i];
    if (seen.live[i] === 1 && hp < seen.hp[i]) {
      const byLeader = lead - seen.leader[i];
      if (!emit(i, seen.hp[i] - hp - byLeader, byLeader)) {
        seen.hp[i] -= byLeader;
        seen.leader[i] = lead;
        if (next < 0) next = i;
        continue;
      }
    }
    seen.hp[i] = hp;
    seen.leader[i] = lead;
    seen.live[i] = w.alive[i];
  }
  return next < 0 ? 0 : next;
}

/** 숫자를 글자 번호 목록으로. 표시 한도를 넘는 피해는 9999로 자른다. */
export function digitsOf(n: number): number[] {
  const v = Math.max(0, Math.min(10 ** MAX_DIGITS - 1, Math.floor(n)));
  return String(v).split('').map(Number);
}

function buildDigitTextures(): Texture[] {
  const canvas = document.createElement('canvas');
  canvas.width = CELL_W * 10;
  canvas.height = CELL_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다');
  // 테두리를 먼저 모두 칠하고 그 위에 글자를 얹는다. 글자는 흰색이라 스프라이트 tint가 그대로 색이 된다.
  for (const [pass, color] of [
    [0, '#1a140e'],
    [1, '#ffffff'],
  ] as const) {
    ctx.fillStyle = color;
    for (let d = 0; d < 10; d++) {
      const g = GLYPHS[d];
      for (let k = 0; k < GW * GH; k++) {
        if (g[k] !== '1') continue;
        const x = d * CELL_W + 1 + (k % GW);
        const y = 1 + Math.floor(k / GW);
        if (pass === 0) ctx.fillRect(x - 1, y - 1, 3, 3);
        else ctx.fillRect(x, y, 1, 1);
      }
    }
  }
  const base = Texture.from(canvas);
  base.source.scaleMode = 'nearest';
  const out: Texture[] = [];
  for (let d = 0; d < 10; d++) {
    out.push(new Texture({ source: base.source, frame: new Rectangle(d * CELL_W, 0, CELL_W, CELL_H) }));
  }
  return out;
}

interface Float {
  box: Container;
  digits: Sprite[];
  t0: number;
  /** 맞은 자리(월드 픽셀). 카메라가 움직이므로 프레임마다 화면으로 다시 옮긴다(`update`). */
  x: number;
  y: number;
  /** 흩뜨림(층 좌표). 화면에서의 좌우·위아래라 가로 화면에서도 숫자가 옆으로 새지 않는다. */
  jx: number;
  jy: number;
  /** 제 크기(정수배). 장수 숫자는 2. */
  size: number;
  color: number;
  /** 쓰러뜨린 한 방이라 번쩍이는가. */
  flash: boolean;
  /** 지금 흰빛으로 칠해 두었는가. 바뀔 때만 글자 색을 다시 칠한다. */
  lit: boolean;
}

export class DamageFloats {
  readonly layer = new Container();
  private readonly tex: Texture[];
  private readonly pool: Float[] = [];
  private cursor = 0;
  private budget = 0;
  /** `update`의 투영 그릇. */
  private readonly at = { x: 0, y: 0 };
  /** 이번 프레임에 뜬 장수 숫자 수. 겹치지 않게 쌓는 데 쓴다. */
  private bigInFrame = 0;

  constructor() {
    this.tex = buildDigitTextures();
    for (let i = 0; i < MAX_FLOATS; i++) {
      const box = new Container();
      box.visible = false;
      const digits: Sprite[] = [];
      for (let k = 0; k < MAX_DIGITS; k++) {
        const s = new Sprite(this.tex[0]);
        s.anchor.set(0.5, 1);
        box.addChild(s);
        digits.push(s);
      }
      this.layer.addChild(box);
      this.pool.push({ box, digits, t0: 0, x: 0, y: 0, jx: 0, jy: 0, size: 1, color: 0xffffff, flash: false, lit: false });
    }
  }

  /** 프레임 첫머리에 부른다. 이번 프레임에 새로 띄울 수 있는 수를 채운다. */
  beginFrame(): void {
    this.budget = FLOAT_BUDGET;
    this.bigInFrame = 0;
  }

  /**
   * (x, y)는 맞은 병사의 발 위치(월드 픽셀). 이번 프레임 한도가 찼으면 띄우지 않고 false.
   * `big`(장수가 친 피해)은 두 배 크기로 띄우고 한도를 쓰지 않는다 — 휩쓸기 한 번에 많아야 `CLEAVE_MAX`(8)명이라
   * 저절로 묶이고, 난전에서 병사들 숫자에 밀려 장수 숫자가 빠지면 안 된다.
   * `flash`면 쓰러뜨린 한 방이라 처음에 흰빛으로 번쩍인다(`flashLit`).
   */
  spawn(x: number, y: number, amount: number, color: number, now: number, big = false, flash = false): boolean {
    if (amount <= 0) return true;
    if (!big && this.budget <= 0) return false;
    if (!big) this.budget--;
    const f = this.pool[this.cursor];
    this.cursor = (this.cursor + 1) % MAX_FLOATS;
    const ds = digitsOf(amount);
    const width = (ds.length - 1) * ADVANCE;
    for (let k = 0; k < MAX_DIGITS; k++) {
      const s = f.digits[k];
      if (k >= ds.length) {
        s.visible = false;
        continue;
      }
      s.visible = true;
      s.texture = this.tex[ds[k]];
      s.tint = flash ? 0xffffff : color;
      s.x = k * ADVANCE - width / 2;
      s.y = 0;
    }
    // 한 자리에 여럿이 맞아도 숫자가 한 점에 겹치지 않게 조금씩 흩뜨린다.
    f.x = x;
    f.y = y;
    f.jx = Math.round(Math.random() * 8 - 4);
    f.jy = Math.round(-HEAD_PX + (Math.random() * 4 - 2));
    if (big) {
      // 장수 숫자는 두 배로 커서, 휩쓸기에 몰려 선 적들이 한꺼번에 맞으면 큰 숫자끼리 겹쳐 읽히지 않았다.
      // 같은 프레임에 뜬 순서대로 한 줄(글자 높이)씩 위로, 좌우를 번갈아 비켜 쌓는다.
      const n = this.bigInFrame++;
      f.jx += n % 2 === 0 ? -BIG_STAGGER_X : BIG_STAGGER_X;
      f.jy -= n * BIG_STAGGER_Y;
    }
    f.t0 = now;
    f.size = big ? 2 : 1;
    f.color = color;
    f.flash = flash;
    f.lit = flash;
    f.box.visible = true;
    // 새로 뜬 것이 위에 오게.
    this.layer.addChild(f.box);
    return true;
  }

  /**
   * `project`는 월드 픽셀을 이 층의 좌표로 옮긴다(렌더러가 카메라의 자리·회전으로 편다). 층은 카메라
   * 밖에 있어 가로 화면에서도 숫자가 똑바로 서고 화면 위로 떠오른다.
   */
  update(now: number, project: (x: number, y: number, out: { x: number; y: number }) => void): void {
    const at = this.at;
    for (const f of this.pool) {
      if (!f.box.visible) continue;
      const t = now - f.t0;
      if (t >= LIFE_MS) {
        f.box.visible = false;
        continue;
      }
      const k = t / LIFE_MS;
      // 처음에 빨리 떠오르고 점점 느려진다.
      const rise = RISE_PX * (1 - (1 - k) * (1 - k));
      project(f.x, f.y, at);
      f.box.x = Math.round(at.x + f.jx);
      f.box.y = Math.round(at.y + f.jy - rise);
      // 도트가 뭉개지지 않게 크기는 정수배로만: 막 떴을 때 한 배 더 크게, 그 뒤 제 크기. 장수 숫자는 튀지 않는다 —
      // 세 배로 튀는 숫자가 휩쓸기마다 여럿이면 화면을 덮었다. 쓰러뜨린 한 방은 번쩍임으로 가른다.
      f.box.scale.set(t < POP_MS && f.size === 1 ? 2 : f.size);
      if (f.flash) {
        const lit = flashLit(t);
        if (lit !== f.lit) {
          f.lit = lit;
          for (const s of f.digits) s.tint = lit ? 0xffffff : f.color;
        }
      }
      f.box.alpha = k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4;
    }
  }
}
